/*
 * Cart upsell "Add" buttons.
 * Mirrors Horizon's product-form flow: dispatch CartLinesUpdateEvent with a deferred
 * promise, add via /cart/add.js with the cart section ids, then resolve so the
 * cart drawer, cart page and cart icon re-render themselves.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { QuickAddComponent } from '@theme/quick-add';

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-wb-upsell-add]') : null;
  if (!button || button.disabled) return;
  event.preventDefault();

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  button.disabled = true;
  button.classList.add('is-loading');

  const routes = (window.Theme && window.Theme.routes) || {};
  const cartAddUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = routes.cart_url || '/cart';

  try {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    const cartResponse = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'wb-cart-upsell',
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[Winterborn] Upsell add failed', error);
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({
        error: (error && error.message) || 'Add to cart failed',
        code: 'INVALID',
      })
    );
    button.disabled = false;
  } finally {
    button.classList.remove('is-loading');
  }
});

/*
 * Cart upsell "Choose" buttons: open the quick-add popup for products with options, so the
 * shopper can pick a colour and add it without leaving the cart.
 * Reuses Horizon's QuickAddComponent on a hidden element that knows the product address.
 */
const quickAddByUrl = new Map();

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-wb-upsell-choose]') : null;
  if (!link || !(link instanceof HTMLAnchorElement)) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const url = link.href;
  let quickAdd = quickAddByUrl.get(url);

  if (!quickAdd) {
    quickAdd = new QuickAddComponent();
    quickAdd.hidden = true;
    quickAdd.dataset.productTitle = link.dataset.productTitle || '';
    Object.defineProperty(quickAdd, 'productPageUrl', { get: () => url });
    document.body.appendChild(quickAdd);
    quickAddByUrl.set(url, quickAdd);
  }

  link.classList.add('is-loading');
  Promise.resolve(quickAdd.handleClick(event)).finally(() => link.classList.remove('is-loading'));
});
